import { useEffect, useMemo, useState } from 'react';
import { Building2 } from 'lucide-react';
import { useSandboxCreateCompany } from '@/services/mutations/sandbox';
import {
	SandboxChipToggle,
	SandboxField,
	SandboxModal,
	SandboxNotice,
	sandboxInputClass,
	sandboxSelectClass,
} from './SandboxModal';
import { formatMoney, fullName } from '../sandboxLabels';

interface Props {
	scenarioId: string;
	catalog: Sandbox.Catalog;
	users: Sandbox.ScenarioUser[];
	isOpen: boolean;
	onClose: () => void;
}

/** Empresa de teste com clube B2B2C e colaboradores vinculados por CPF. */
const SandboxCompanyModal = ({
	scenarioId,
	catalog,
	users,
	isOpen,
	onClose,
}: Props) => {
	const b2b2cClubs = useMemo(
		() => catalog.clubs.filter((c) => c.shelf === 'B2B2C'),
		[catalog.clubs],
	);
	const eligibleUsers = useMemo(
		() => users.filter((u) => u.cpf && !u.employee),
		[users],
	);
	const [tradeName, setTradeName] = useState('');
	const [clubId, setClubId] = useState('');
	const [employeeIds, setEmployeeIds] = useState<string[]>([]);

	useEffect(() => {
		if (!isOpen) return;
		setTradeName('');
		setClubId('');
		setEmployeeIds([]);
	}, [isOpen]);

	const { mutate, isPending } = useSandboxCreateCompany(scenarioId, onClose);

	return (
		<SandboxModal
			isOpen={isOpen}
			onClose={onClose}
			title="Criar empresa"
			description="Cadastra a empresa (CompanyService), vincula um clube B2B2C e cadastra colaboradores por CPF — o vínculo com a conta do usuário é automático."
			icon={<Building2 className="h-4 w-4" />}
			confirmText="Criar empresa"
			onConfirm={() =>
				mutate({
					trade_name: tradeName.trim() || undefined,
					club_id: clubId || undefined,
					employee_user_ids:
						employeeIds.length > 0 ? employeeIds : undefined,
				})
			}
			isPending={isPending}
		>
			<div className="space-y-4 py-2">
				<SandboxField label="Nome fantasia">
					<input
						className={sandboxInputClass}
						value={tradeName}
						onChange={(e) => setTradeName(e.target.value)}
						placeholder="gerado"
					/>
				</SandboxField>
				<SandboxField
					label="Clube B2B2C"
					hint={
						b2b2cClubs.length === 0
							? 'Nenhum clube na prateleira B2B2C. Opcional.'
							: 'Opcional. Só clubes B2B2C podem ser vinculados.'
					}
				>
					<select
						className={sandboxSelectClass}
						value={clubId}
						onChange={(e) => setClubId(e.target.value)}
					>
						<option value="">sem clube</option>
						{b2b2cClubs.map((c) => (
							<option key={c.id} value={c.id}>
								{c.name} · {formatMoney(c.price)}/mês
							</option>
						))}
					</select>
				</SandboxField>
				<SandboxField
					label="Colaboradores (usuários do cenário com CPF)"
					hint={
						eligibleUsers.length === 0
							? 'Nenhum usuário disponível para vincular.'
							: undefined
					}
				>
					<SandboxChipToggle
						options={eligibleUsers.map((u) => ({
							key: u.id,
							label: fullName(u),
						}))}
						selected={employeeIds}
						onToggle={(id) =>
							setEmployeeIds((prev) =>
								prev.includes(id)
									? prev.filter((x) => x !== id)
									: [...prev, id],
							)
						}
					/>
				</SandboxField>
				<SandboxNotice>
					A ativação automática B2B2C só acontece com plano
					empresarial ativo e subsídio integral — sem isso, o
					colaborador fica vinculado mas sem assinatura (regra real).
				</SandboxNotice>
			</div>
		</SandboxModal>
	);
};

export { SandboxCompanyModal };
